import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { api } from "@/api/api";
import type {
  InitiateFlightsSearchBodyInputDto,
  InitiateFlightsSearchOutputDto,
} from "@/types/codegen";
import { StopsOptions, TripType } from "@/types/enums";
import type { TFlightFormValues } from "@/types/types";

import { useFlightSearchDataContext } from "../context/FlightSearchDataContext";
import { SearchDataMissingError } from "../errors/SearchDataMissingError";
import { initialSortValue } from "../initialFlightsValues";
import { getDateString } from "../utils/dates";

export const PAGE_SIZE = 20;

export const flightSearchDataMapper = (
  data: TFlightFormValues,
): InitiateFlightsSearchBodyInputDto | null => {
  if (!data.from || !data.to) {
    return null;
  }

  const transformedData: InitiateFlightsSearchBodyInputDto = {
    originDestinations: [
      {
        origin: {
          id: data.from.id,
          type: data.from.type,
        },
        destination: {
          id: data.to.id,
          type: data.to.type,
        },
        date: getDateString(data.dates.departure),
      },
    ],
    cabinClass: data.cabinClass,
    passengers: {
      passengerTypeCodeList: [],
    },
  };

  if (data.tripType === TripType.ROUND_TRIP) {
    transformedData.originDestinations.push({
      origin: {
        id: data.to.id,
        type: data.to.type,
      },
      destination: {
        id: data.from.id,
        type: data.from.type,
      },
      // telling TS that if tripType is round trip, this field will not be undefined
      date: getDateString(data.dates.return!),
    });
  }

  if (data.adult) {
    transformedData.passengers.passengerTypeCodeList.push({
      passengerTypeCode: "ADT",
      count: data.adult,
    });
  }

  if (data.child) {
    transformedData.passengers.passengerTypeCodeList.push({
      passengerTypeCode: "CHD",
      count: data.child,
    });
  }

  if (data.infantWithoutSeat) {
    transformedData.passengers.passengerTypeCodeList.push({
      passengerTypeCode: "INF",
      count: data.infantWithoutSeat,
    });
  }

  return transformedData;
};

type Params = { perPage: number; sortField: string; sortOrder: string };

const params: Params = {
  perPage: PAGE_SIZE,
  // pass default values here, as this query should not be refetched when sort changes
  // because it's used to get search tokens, not actual results
  sortField: initialSortValue.field,
  sortOrder: initialSortValue.order,
};

const selectData = (
  response: AxiosResponse<InitiateFlightsSearchOutputDto>,
) => {
  const data = response.data.data;

  return {
    searchResultKey: data.searchResultKey,
    searchResultToken: data.searchResultToken,
    defaultFilters: {
      stops: StopsOptions.ANY,
      priceRange: [data.minPrice || 1, data.maxPrice || 1] satisfies [
        number,
        number,
      ],
      selectedAirlines: data.carriers.map((c) => c.code),
      airlines: data.carriers,
    },
  };
};

const quickSearchQueryOptions = (
  payload: InitiateFlightsSearchBodyInputDto | null,
) =>
  queryOptions({
    queryKey: [
      "flights",
      "search",
      "quick",
      "tokens",
      payload,
      params,
    ] as const,
    queryFn: ({ signal }) =>
      api.post<InitiateFlightsSearchOutputDto>(
        `/flights/search/quick`,
        payload,
        {
          params,
          signal,
        },
      ),
    select: selectData,
    enabled: Boolean(payload),
    gcTime: 0,
    retry: false,
  });

export const searchQueryOptions = (
  payload: InitiateFlightsSearchBodyInputDto | null,
  enabled: boolean = true,
) =>
  queryOptions({
    queryKey: ["flights", "search", "tokens", payload, params] as const,
    queryFn: ({ signal }) =>
      api.post<InitiateFlightsSearchOutputDto>(`/flights/search`, payload, {
        params,
        signal,
      }),
    select: selectData,
    enabled: Boolean(payload) && enabled,
    gcTime: 0,
  });

export const useFlightSearchTokens = (
  options: { enabled: boolean } = { enabled: true },
) => {
  const { searchData } = useFlightSearchDataContext();
  const queryClient = useQueryClient();

  if (!searchData) {
    throw new SearchDataMissingError("Flight search data is missing");
  }

  const queryOptions = searchQueryOptions(
    flightSearchDataMapper(searchData),
    options.enabled,
  );

  return {
    query: useQuery(queryOptions),
    cancel: () =>
      queryClient.cancelQueries({ queryKey: queryOptions.queryKey }),
  };
};

export const useFlightQuickSearchTokens = () => {
  const { searchData } = useFlightSearchDataContext();

  if (!searchData) {
    throw new SearchDataMissingError("Flight search data is missing");
  }

  return useQuery(quickSearchQueryOptions(flightSearchDataMapper(searchData)));
};
